<template>
  <section class="section">
    <div class="container">
      <!-- Title -->
      <h1 class="title is-4">
        <i class="fas fa-file-invoice"></i>&nbsp; Peppol Document Details
      </h1>
      <o-button variant="info" icon-left="eye" @click="previewDocument">
        Preview Peppol
      </o-button>
      <!-- Loader -->
      <div v-if="loading" class="has-text-centered py-6">
        <i class="fas fa-spinner fa-spin fa-2x"></i>
        <p class="mt-2">Loading document...</p>
      </div>

      <!-- Document details -->
      <div v-else-if="document">
        <o-notification variant="info" class="mb-4" closable>
          <strong>Type:</strong> {{ document.type || '—' }} |
          <strong>Direction:</strong> {{ document.direction || '—' }} |
          <strong>Issue Date:</strong> {{ formatDate(document.issue_date) }}
          <strong>Due Date:</strong> {{ formatDate(document.due_date) }}
        </o-notification>

        <!-- Seller & Buyer -->
        <div class="columns">
          <div class="column">
            <h2 class="subtitle is-5">📤 Seller</h2>
            <p>
              <strong>{{ document.seller?.name || '—' }}</strong>
            </p>
            <p>{{ document.seller?.street }}</p>
            <p>
              {{ document.seller?.postalZone }}
              {{ document.seller?.city }}
              {{ document.seller?.country }}
            </p>
            <p>VAT: {{ document.seller?.vatNumber || '—' }}</p>
          </div>

          <div class="column">
            <h2 class="subtitle is-5">📥 Buyer</h2>
            <p>
              <strong>{{ document.buyer?.name || '—' }}</strong>
            </p>
            <p>{{ document.buyer?.street }}</p>
            <p>
              {{ document.buyer?.postalZone }}
              {{ document.buyer?.city }}
              {{ document.buyer?.country }}
            </p>
            <p>VAT: {{ document.buyer?.vatNumber || '—' }}</p>
          </div>
        </div>

        <hr />

        <!-- Items table -->
        <h2 class="subtitle is-5">📦 Items</h2>
        <table class="table is-fullwidth is-striped is-hoverable">
          <thead>
            <tr>
              <th>#</th>
              <th>Item name</th>
              <th>Description</th>
              <th>Qty</th>
              <th>Unit price (€)</th>
              <th>VAT (%)</th>
              <th>Total (€)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in document.items || []" :key="index">
              <td>{{ index + 1 }}</td>
              <td>{{ item.name || '—' }}</td>
              <td>{{ item.description || '—' }}</td>
              <td>{{ item.quantity || 1 }}</td>
              <td>{{ item.unit_price || '—' }}</td>
              <td>{{ item.vat_percentage || 0 }}</td>
              <td>
                {{
                  item.net_amount ||
                  (
                    parseFloat(item.quantity || 1) *
                    parseFloat(item.unit_price || 0)
                  ).toFixed(2)
                }}
              </td>
            </tr>
          </tbody>
        </table>

        <hr />

        <!-- Totals -->
        <h2 class="subtitle is-5">💰 Totals</h2>
        <p>Net: € {{ document.totals?.taxExclusiveAmount || '—' }}</p>
        <p>VAT: € {{ document.vat?.totalVatAmount || '—' }}</p>
        <p>
          <strong>
            Gross: € {{ document.totals?.taxInclusiveAmount || '—' }}
          </strong>
        </p>

        <hr />

        <!-- Actions -->
        <div class="buttons mt-5">
          <o-button
            class="button is-primary"
            variant="success"
            size="medium"
            icon-left="file-plus"
            :loading="isCreating"
            @click="createDocumentDraft"
          >
            Create Document (draft)
          </o-button>
        </div>
      </div>

      <!-- No document -->
      <div v-else class="has-text-centered has-text-grey">
        <p>No document found.</p>
      </div>
    </div>
    <div
      v-if="toast.show"
      class="notification"
      :class="toast.type"
      style="position: fixed; bottom: 20px; right: 20px; z-index: 9999"
    >
      {{ toast.message }}
    </div>
  </section>
  <div class="modal" :class="{ 'is-active': showPreview }">
    <div class="modal-background" @click="showPreview = false"></div>

    <div class="modal-card" style="width: 90%">
      <header class="modal-card-head">
        <p class="modal-card-title">Peppol Preview</p>
        <button class="delete" @click="showPreview = false"></button>
      </header>

      <section class="modal-card-body">
        <iframe
          v-if="previewHtml"
          :srcdoc="previewHtml"
          style="width: 100%; height: 80vh; border: none"
        ></iframe>
      </section>
    </div>
  </div>
</template>

<script>
import api from '@/utils/axios';
import { useOruga } from '@oruga-ui/oruga-next';

export default {
  name: 'PeppolDocumentDetail',
  setup() {
    const oruga = useOruga();
    return { oruga };
  },
  data() {
    return {
      document: null,
      loading: false,
      toast: {
        show: false,
        message: '',
        type: 'is-success',
      },
      previewHtml: '',
      showPreview: false,
    };
  },
  methods: {
    async fetchDocument() {
      this.loading = true;
      try {
        const id = this.$route.params.id;
        const response = await api.get(`/peppol/documents/${id}/`);
        // ✅ The backend now returns { success: true, document: {...} }
        this.document = response.data.document || response.data;
      } catch (error) {
        console.error('❌ Error fetching document details:', error);
        this.oruga.notification.open({
          message: 'Failed to load Peppol document details.',
          variant: 'danger',
          position: 'bottom-right',
        });
      } finally {
        this.loading = false;
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '—';
      return new Date(dateStr).toLocaleDateString('fr-BE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
    showToast(message, type = 'is-success') {
      this.toast.message = message;
      this.toast.type = type;
      this.toast.show = true;
      setTimeout(() => {
        this.toast.show = false;
      }, 3000);
    },

    async createDocumentDraft() {
      try {
        const payload = {
          document: this.document, // 👈 TOUT le document Peppol normalisé
        };

        const res = await api.post('/peppol/create-document-draft/', payload);

        this.showToast(
          `Document created for ${res.data.third_party_name}`,
          'is-success'
        );
      } catch (error) {
        console.error('❌ Error creating document draft:', error);
        this.showToast('Failed to create document.', 'is-danger');
      }
    },
    async previewDocument() {
      const id = this.$route.params.id;

      const res = await api.get(`/peppol/documents/${id}/preview/`);

      this.previewHtml = res.data;
      this.showPreview = true;
    },
  },
  mounted() {
    this.fetchDocument();
  },
};
</script>

<style scoped>
.section {
  padding-bottom: 5rem;
}
</style>
